import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchWinbackSupportDetails,
  getAnalyticsEventContext,
  logAnalyticsEvent,
} from './Firebase';
import { decodeWinbackTokenPayload, normalizeWinbackOfferDetails } from './checkoutState';
import { detectCurrentMobilePlatform } from './devicePlatform';

// Mirrors the backend's email CTA OneLink pattern
// (notification_service.py _email_cta_onelink_url_for_result): opens the app
// when installed, falls through to the store otherwise.
const FLUFFBALL_OPEN_APP_ONELINK_URL =
  'https://fluffball.onelink.me/tRJF/email?pid=email&c=winback_checkout_success';

const buildOpenAppOneLinkUrl = (claimToken = '') => {
  const token = String(claimToken || '').trim();
  if (!token) {
    return FLUFFBALL_OPEN_APP_ONELINK_URL;
  }
  const params = new URLSearchParams({
    pid: 'email',
    c: 'winback_checkout_success',
    deep_link_value: 'winback_claim',
    af_sub1: token,
    winback_token: token,
  });
  return `https://fluffball.onelink.me/tRJF/email?${params.toString()}`;
};

const primaryCtaClasses =
  'inline-flex min-h-[58px] w-full items-center justify-center rounded-[18px] bg-gradient-to-r from-[#ec4899] to-[#8b5cf6] px-6 py-3 text-[17px] font-extrabold leading-none text-white shadow-[0_10px_24px_rgba(236,72,153,0.22)] transition duration-200 hover:brightness-105';

const supportLinkClasses =
  'mt-3 inline-flex min-h-[44px] items-center justify-center rounded-full px-4 py-2 text-[14px] font-extrabold text-slate-500 transition duration-200 hover:bg-slate-50 hover:text-slate-700';

const mobileTitleStyle = {
  fontFamily: 'Charter, Georgia, "Times New Roman", serif',
};

const buildSupportMailtoHref = ({ firebaseUid, email, claimToken }) => {
  const uidLine = String(firebaseUid || '').trim() || 'unavailable';
  const emailLine = String(email || '').trim() || 'unavailable';
  const tokenLine = String(claimToken || '').trim() || 'unavailable';
  const body = [
    'Hi Fluffball support,',
    '',
    'I subscribed but need help opening Fluffball Pro.',
    '',
    `Firebase UID: ${uidLine}`,
    `Email: ${emailLine}`,
    `Checkout token: ${tokenLine}`,
  ].join('\n');

  return `mailto:support@fluffball.app?subject=${encodeURIComponent(
    'Fluffball checkout help'
  )}&body=${encodeURIComponent(body)}`;
};

const CheckoutSuccessPage = () => {
  const location = useLocation();
  const viewLoggedRef = useRef(false);
  const [supportInfo, setSupportInfo] = useState({ firebaseUid: '', email: '' });
  const platform = useMemo(() => detectCurrentMobilePlatform(), []);
  const isMobile = platform !== 'desktop';
  const claimToken = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return String(params.get('token') || '').trim();
  }, [location.search]);
  const decodedTokenPayload = useMemo(() => decodeWinbackTokenPayload(claimToken), [claimToken]);
  const firebaseUid = supportInfo.firebaseUid || decodedTokenPayload?.uid || '';
  const supportLinkReady = Boolean(firebaseUid || claimToken);
  const openAppOneLinkUrl = useMemo(() => buildOpenAppOneLinkUrl(claimToken), [claimToken]);
  const supportMailtoHref = useMemo(
    () =>
      buildSupportMailtoHref({
        firebaseUid,
        email: supportInfo.email,
        claimToken,
      }),
    [claimToken, firebaseUid, supportInfo.email]
  );
  const title = isMobile ? 'You are in' : 'Open Fluffball on your phone';
  const body = isMobile
    ? 'Fluffball Pro is active. Open the app to continue.'
    : 'Fluffball Pro is active. Open the app on your phone to continue.';

  const trackSuccessEvent = useCallback(
    (eventName, properties = {}) => {
      logAnalyticsEvent({
        analyticsContext: getAnalyticsEventContext(),
        eventName,
        properties: {
          screen: 'winback_checkout_success',
          platform,
          ...properties,
        },
      });
    },
    [platform]
  );

  useEffect(() => {
    if (viewLoggedRef.current) {
      return;
    }
    viewLoggedRef.current = true;
    trackSuccessEvent('fluffball-winback-checkout-success-viewed');
  }, [trackSuccessEvent]);

  useEffect(() => {
    let cancelled = false;
    const tokenUid = decodedTokenPayload?.uid || '';

    if (!claimToken) {
      setSupportInfo({ firebaseUid: tokenUid, email: '' });
      return undefined;
    }

    setSupportInfo((current) => ({
      firebaseUid: current.firebaseUid || tokenUid,
      email: current.email,
    }));

    fetchWinbackSupportDetails({ token: claimToken })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const normalized = normalizeWinbackOfferDetails(response);
        setSupportInfo({
          firebaseUid: normalized.support.firebaseUid || tokenUid,
          email: normalized.support.email || '',
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setSupportInfo((current) => ({
          firebaseUid: current.firebaseUid || tokenUid,
          email: current.email,
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [claimToken, decodedTokenPayload?.uid]);

  return (
    <main className="min-h-screen min-h-[100dvh] bg-white text-[#1e293b]">
      <div className="mx-auto flex min-h-screen min-h-[100dvh] max-w-[430px] items-center px-4 py-[calc(env(safe-area-inset-top)+24px)]">
        <section className="w-full rounded-[28px] border border-slate-200/80 bg-white p-5 text-center shadow-[0_18px_42px_rgba(15,23,42,0.10)]">
          <p className="mx-auto mb-4 inline-flex rounded-full bg-[#fff7fb] px-4 py-2 text-xs font-extrabold uppercase tracking-normal text-[#ec4899]">
            Fluffball Pro
          </p>
          <h1
            className="mx-auto max-w-[340px] text-[clamp(28px,7.4vw,34px)] font-bold leading-[1.08] tracking-normal text-[#1e293b]"
            style={mobileTitleStyle}
          >
            {title}
          </h1>
          <p className="mx-auto mt-3 max-w-[320px] text-[15px] font-medium leading-6 text-slate-500">
            {body}
          </p>
          <div className="mt-5">
            {isMobile ? (
              <a
                href={openAppOneLinkUrl}
                onClick={() => trackSuccessEvent('fluffball-winback-checkout-success-open-app-clicked')}
                className={primaryCtaClasses}
              >
                Open Fluffball
              </a>
            ) : null}
            {supportLinkReady ? (
              <a
                href={supportMailtoHref}
                onClick={() => trackSuccessEvent('fluffball-winback-checkout-success-support-clicked')}
                className={supportLinkClasses}
              >
                Having trouble?
              </a>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
};

export default CheckoutSuccessPage;
